<template>
  <a-modal v-model:open="openModal" @cancel="closeLoyaltyPopup" centered>
    <template #title>
      <span>{{ t("Pos.Loyalty") }}</span>
    </template>

    <template #footer>
      <a-button key="submit" type="primary" @click="confirm">{{
        t("Pos.Confirm")
      }}</a-button>
      <a-button key="back" @click="closeLoyaltyPopup">{{
        t("Pos.Cancel")
      }}</a-button>
      <a-button key="clear" danger @click="clearLoyaltyConfig">
        {{ t("Pos.ClearLoyalty") }}
      </a-button>
    </template>

    <a-form
      ref="formRef"
      :model="form"
      :rules="rules"
      layout="vertical"
      :class="{ 'is-rtl': textDirection === 'rtl' }"
    >
      <a-row>
        <a-col :span="24">
          <a-form-item name="type">
            <template #label>
              <span class="label-form">{{ t("Pos.Type") }}</span>
            </template>
            <a-select
              v-model:value="form.type"
              :options="state.types"
              @change="handleType"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item name="quantityConsume">
            <template #label>
              <span class="label-form">{{ t("Pos.quantityConsume") }}</span>
            </template>
            <a-input
              v-model:value="form.quantityConsume"
              type="number"
              min="0"
              @change="handleQuantityConsumeChange"
            />
          </a-form-item>
        </a-col>

        <a-col :span="12">
          <a-form-item name="quantityEarn">
            <template #label>
              <span class="label-form">{{ t("Pos.quantityEarn") }}</span>
            </template>
            <a-input v-model:value="form.quantityEarn" type="number" min="0" />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row>
        <a-col :span="24">
          <a-form-item name="availability">
            <template #label>
              <span class="label-form">{{ t("Pos.Availability") }}</span>
            </template>
            <a-range-picker
              class="input-form"
              v-model:value="form.availability"
              :show-time="{ format: 'HH:mm' }"
              format="YYYY-MM-DD HH:mm"
              :placeholder="[t('Pos.startDate'), t('Pos.endDate')]"
              :disabledDate="
                (current) => current && current < dayjs().startOf('day')
              "
            />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row
        v-for="(row, index) in dynamicRows"
        :key="index"
        :gutter="16"
        class="mb-4"
      >
        <a-col :span="20">
          <a-form-item
            :name="['rows', index, 'category']"
            :rules="[{ required: true, message: t('Pos.RequiredField') }]"
          >
            <template #label>
              <span class="label-form"> {{ t("Pos.Type") }}</span>
            </template>
            <a-select
              v-model:value="row.category"
              :options="
                state.data.map((item) => ({ label: item.name, value: item.id }))
              "
            />
          </a-form-item>
        </a-col>

        <a-col :span="4" class="action-container">
          <a-button type="primary" @click="addRow">
            <template #icon><plus-outlined /></template>
          </a-button>
          <a-button
            type="primary"
            danger
            @click="removeRow(index)"
            :disabled="dynamicRows.length <= 1"
          >
            <template #icon><delete-outlined /></template>
          </a-button>
        </a-col>
      </a-row>
    </a-form>
  </a-modal>
</template>

<script lang="ts">
import { categoryService, productService, storeService } from "@/_services";
import {
  defineComponent,
  defineProps,
  defineEmits,
  ref,
  reactive,
  computed,
  onMounted,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import { Rule } from "ant-design-vue/es/form";
import dayjs from "dayjs";

export default defineComponent({
  name: "LoyaltyPopup",
  components: {
    PlusOutlined,
    DeleteOutlined,
  },
});
</script>

<script setup lang="ts">
const { t, locale } = useI18n();
const props = defineProps<{
  open: boolean;
  store: any;
}>();

const openModal = computed(() => {
  return props.open;
});

const emit = defineEmits([
  "closeLoyaltyPopup",
  "showSuccessNotification",
  "refreshStores",
  "loading",
]);

const closeLoyaltyPopup = () => {
  emit("closeLoyaltyPopup");
  form.quantityConsume = 0;
  form.quantityEarn = 0;
  form.type = "category";
  form.availability = [];
  dynamicRows.value = [
    {
      category: "",
    },
  ];
  form.rows = [...dynamicRows.value];
};

const state = reactive({
  types: [
    {
      label: t("Pos.Category"),
      value: "category",
    },
    {
      label: t("Pos.Product"),
      value: "product",
    },
  ],
  data: [] as any[],
});

const formRef = ref();
const form = reactive({
  quantityConsume: 0,
  quantityEarn: 0,
  type: "category",
  rows: [] as any[],
  availability: [] as any[],
});

const rules: Record<string, Rule[]> = {
  quantityConsume: [{ required: true, message: t("Pos.RequiredField") }],
  quantityEarn: [{ required: true, message: t("Pos.RequiredField") }],
  type: [{ required: true, message: t("Pos.RequiredField") }],
  "rows.0.category": [{ required: true, message: t("Pos.RequiredField") }],
  availability: [{ required: true, message: t("Pos.RequiredField") }],
};
const dynamicRows = ref([
  {
    category: "",
  },
]);

const textDirection = computed(() => {
  return locale.value === "ar" ? "rtl" : "ltr";
});

const loadingData = ref(false);

const getCategories = async () => {
  try {
    loadingData.value = true;
    const res = await categoryService.getAll();
    state.data = res.data;
  } catch (error) {
    console.log(error);
  } finally {
    loadingData.value = false;
  }
};

const getProducts = async () => {
  try {
    loadingData.value = true;
    const res = await productService.getAll();
    state.data = res.data;
  } catch (error) {
    console.log(error);
  } finally {
    loadingData.value = false;
  }
};

const handleType = async (value: string) => {
  if (value === "category") {
    await getCategories();
  } else {
    await getProducts();
  }
};

const handleQuantityConsumeChange = (value: number) => {
  while (dynamicRows.value.length > value) {
    dynamicRows.value.pop();
  }
  form.rows = [...dynamicRows.value];
};

const addRow = () => {
  // if (dynamicRows.value.length < form.quantityConsume) {
  dynamicRows.value.push({
    category: "",
  });
  form.rows = [...dynamicRows.value];
  // }
};

const removeRow = (index: number) => {
  if (dynamicRows.value.length > 1) {
    dynamicRows.value.splice(index, 1);
    form.rows = [...dynamicRows.value];
  }
};

const confirm = () => {
  formRef.value
    .validate()
    .then(async () => {
      try {
        emit("loading", true);

        let loyaltyConfig = {
          quantityConsume: form.quantityConsume,
          quantityEarn: form.quantityEarn,
          type: form.type,
          items: dynamicRows.value,
          availability: form.availability
            ? [
                form.availability[0].format("YYYY-MM-DD HH:mm"),
                form.availability[1].format("YYYY-MM-DD HH:mm"),
              ]
            : null,
        };

        // Safely parse the store config with fallbacks
        let currentConfig: any = {};
        try {
          currentConfig = props.store?.config ? JSON.parse(props.store.config) : {};
        } catch (error) {
          console.error('Error parsing store config:', error);
          currentConfig = {};
        }

        const config = {
          order_config: currentConfig.order_config || {},
          loyalty_config: loyaltyConfig,
        };

        const formData = new FormData();
        formData.append("name", props.store.name);
        formData.append("description", props.store.description);
        formData.append("address", props.store.address);
        formData.append("email", props.store.email);
        formData.append("phone", props.store.phone);
        formData.append("logo", props.store.logo);
        formData.append("account_id", props.store.account_id);
        formData.append("config", JSON.stringify(config));
        await storeService.modify(props.store.id, formData);

        emit("refreshStores");
        emit("loading", false);
        emit("showSuccessNotification", t("Pos.LoyaltyUpdatedSuccessfully"));
        closeLoyaltyPopup();
      } catch (error) {
        console.log("error update store", error);
        emit("loading", false);
      }
    })
    .catch((error: any) => {
      console.log(error);
      emit("loading", false);
      closeLoyaltyPopup();
    });
};

const clearLoyaltyConfig = async () => {
  try {
    emit("loading", true);

    // Get current store config
    const currentConfig = props.store?.config
      ? JSON.parse(props.store.config)
      : {};

    // Remove loyalty_config from the config
    delete currentConfig.loyalty_config;

    // Update the store with the modified config
    // const updatedStore = {
    //   ...props.store,
    //   config: JSON.stringify(currentConfig)
    // };

    const config = {
      order_config: JSON.parse(props.store.config).order_config,
      loyalty_config: {},
    };

    const formData = new FormData();
    formData.append("name", props.store.name);
    formData.append("description", props.store.description);
    formData.append("address", props.store.address);
    formData.append("email", props.store.email);
    formData.append("phone", props.store.phone);
    formData.append("logo", props.store.logo);
    formData.append("account_id", props.store.account_id);
    formData.append("config", JSON.stringify(config));
    await storeService.modify(props.store.id, formData);

    // Reset form
    form.quantityConsume = 0;
    form.quantityEarn = 0;
    form.type = "category";
    form.availability = [];
    dynamicRows.value = [
      {
        category: "",
      },
    ];

    emit("loading", false);
    closeLoyaltyPopup();
  } catch (error) {
    console.error("Error clearing loyalty config:", error);
    emit("loading", false);
  }
};

onMounted(async () => {
  await getCategories();
});

watch(
  () => props.store,
  (newStore) => {
    if (newStore && newStore.config) {
      console.log("newStore",newStore)
      try {
        const config = JSON.parse(newStore.config);
        if (config.loyalty_config) {
          const loyaltyConfig = config.loyalty_config;

          // Set form values
          form.quantityConsume = loyaltyConfig.quantityConsume;
          form.quantityEarn = loyaltyConfig.quantityEarn;
          form.type = loyaltyConfig.type;

          // Set availability dates if they exist
          if (loyaltyConfig.availability) {
            form.availability = [
              dayjs(loyaltyConfig.availability[0]),
              dayjs(loyaltyConfig.availability[1]),
            ];
          }

          // Set dynamic rows
          if (loyaltyConfig.items && Array.isArray(loyaltyConfig.items)) {
            dynamicRows.value = loyaltyConfig.items;
            form.rows = [...loyaltyConfig.items];
          }

          // Load the appropriate data based on type
          handleType(loyaltyConfig.type);
        }
      } catch (error) {
        console.error("Error parsing store config:", error);
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.action-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.input-form {
  width: 100%;
}
</style>

